import { useEffect, useState, type FormEvent } from 'react';
import { BrandLogo } from '../components/BrandLogo';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { companyAdapter } from '../data/context';
import { ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';

export function Login({ administrative = false }: { administrative?: boolean }) {
  const { status, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [signup, setSignup] = useState(false);
  const [recovery, setRecovery] = useState(false);
  useEffect(() => {
    if (administrative) return;
    companyAdapter.options().then((o) => { setSignup(o.signupEnabled); setRecovery(!!o.passwordRecovery); }).catch(() => undefined);
  }, [administrative]);
  const [busy, setBusy] = useState(false);
  const from = (location.state as { from?: string } | null)?.from ?? '/';

  if (status === 'authenticated') return <Navigate to={from} replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError && err.status === 429 ? 'Muitas tentativas. Aguarde um minuto e tente novamente.'
        : err instanceof ApiError && err.status < 500 ? 'E-mail ou senha inválidos.' : 'Não foi possível acessar agora. Tente novamente.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="login">
      <div className="login-panel">
        <BrandLogo variant="public" />
        <span className="eyebrow">{administrative ? 'Administração global da plataforma' : 'Plataforma de inteligência comercial'}</span>
        <h1>{administrative ? 'Acesso administrativo protegido.' : 'Seu próximo movimento começa com o sinal certo.'}</h1>
        <p>{administrative ? 'Use exclusivamente sua identidade administrativa dedicada. O segundo fator será exigido na próxima etapa.' : 'Acompanhe o mercado, priorize contas e transforme evidências em decisões comerciais claras.'}</p>
        <form className="form" onSubmit={submit} noValidate>
          {error && <div className="form-error" role="alert">{error}</div>}
          <div className="field"><label htmlFor="email">E-mail corporativo</label>
            <input id="email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} aria-invalid={!!error} /></div>
          <div className="field"><label htmlFor="password">Senha</label>
            <input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} aria-invalid={!!error} /></div>
          <button className="primary" type="submit" disabled={busy}>{busy ? 'Acessando…' : 'Acessar'}</button>
        </form>
        {/* IM-UX-188 (SAAS-004): só oferece a recuperação por e-mail quando ela funciona de verdade. */}
        <div className="login-note">{administrative ? 'Perdeu a senha e os códigos? Use o procedimento de recuperação por SSH.' : recovery ? <Link to="/recuperar-acesso">Esqueci minha senha</Link> : 'Esqueceu a senha? Fale com o administrador da sua organização.'}</div>
        {!administrative && <div className="login-note">{signup ? <>Ainda não tem conta? <Link to="/cadastro">Criar conta</Link></> : 'Acesso restrito a usuários convidados pela organização.'}</div>}
      </div>
      <div className="login-visual" aria-hidden="true">
        <div className="signal-map">
          <h2>Do mercado ao<br /><span>próximo movimento.</span></h2>
          <p>Sinais dispersos ganham contexto, prioridade e direção para o time comercial.</p>
          <div className="motion-path">
            <svg viewBox="0 0 700 250" preserveAspectRatio="none">
              <defs><linearGradient id="line" x1="0" x2="1"><stop stopColor="#315e5f" /><stop offset="1" stopColor="#6df0cb" /></linearGradient><linearGradient id="fill" x1="0" y1="0" x2="0" y2="1"><stop stopColor="#6df0cb" stopOpacity=".22" /><stop offset="1" stopColor="#6df0cb" stopOpacity="0" /></linearGradient></defs>
              <path d="M0 220 C90 215 120 210 185 194 S278 172 330 178 S412 142 470 120 S560 82 700 22 L700 250 L0 250Z" fill="url(#fill)" />
              <path d="M0 220 C90 215 120 210 185 194 S278 172 330 178 S412 142 470 120 S560 82 700 22" fill="none" stroke="url(#line)" strokeWidth="4" />
              <g fill="#6df0cb"><circle cx="185" cy="194" r="6" /><circle cx="330" cy="178" r="6" /><circle cx="470" cy="120" r="6" /><circle cx="700" cy="22" r="7" /></g><circle cx="560" cy="82" r="7" fill="#fdc44f" />
            </svg>
          </div>
          <div className="mini-pulse"><div><b>Sinal</b><span>evidências do mercado</span></div><div><b>Síntese</b><span>contexto e prioridade</span></div><div><b>Movimento</b><span>próximo passo comercial</span></div></div>
        </div>
      </div>
    </section>
  );
}
